import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { TrendingTag } from "@/features/trending/types/TrendingTags";
import { TrendingUp, X, ChevronDown } from "lucide-react";

interface TrendingTagsProps {
  tags: TrendingTag[];
  isLoadingTags: boolean;
  loadTrendingTags: () => void;
  tagsDateRange: number;
}

interface CliffNote {
  main_point?: string;
  why_care?: string;
  key_facts?: Array<{ fact: string; source: string }>;
  whats_next?: string[];
}
interface Article {
  title: string;
  link: string;
  author: string;
}

const parseThis = (cliffNoteStr: string | null) => {
  if (!cliffNoteStr) return null;
  try {
    const parsed = JSON.parse(cliffNoteStr);
    // If parsed is a string, it might be double-encoded JSON
    if (typeof parsed === "string") {
      try {
        const doubleDecoded = JSON.parse(parsed);
        console.log("double-decoded:", doubleDecoded);
        return doubleDecoded;
      } catch (innerError) {
        console.error("Failed to double-decode:", innerError);
      }
    }
    return parsed;
  } catch (error) {
    console.error("Error parsing cliff note:", error);
    return null;
  }
};

export default function TrendingTags({
  tags,
  isLoadingTags,
  loadTrendingTags,
}: TrendingTagsProps) {
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [tagColors, setTagColors] = useState<{ [key: string]: string }>({});
  const [showCliffNote, setShowCliffNote] = useState<boolean>(false);
  const [showArticles, setShowArticles] = useState<boolean>(false);
  const [cliffNote, setCliffNote] = useState<CliffNote | null>(null);
  const [articles, setArticles] = useState<Article[]>([]);

  useEffect(() => {
    loadTrendingTags();
  }, [loadTrendingTags]);

  useEffect(() => {
    const pastelGradients = [
      "from-pink-200 to-purple-200",
      "from-indigo-200 to-blue-200",
      "from-cyan-200 to-teal-200",
      "from-green-200 to-lime-200",
      "from-yellow-200 to-amber-200",
      "from-orange-200 to-red-200",
    ];
    const newTagColors: { [key: string]: string } = {};
    tags.forEach((tag) => {
      newTagColors[tag.tag] =
        pastelGradients[Math.floor(Math.random() * pastelGradients.length)];
    });
    setTagColors(newTagColors);
  }, [tags]); // Only run when `tags` changes

  const handleTagClick = (tag: TrendingTag) => {
    setShowArticles(false);

    // Handle different possible types of cliff_note
    let parsedNote = null;
    if (typeof tag.cliff_note === "string") {
      parsedNote = parseThis(tag.cliff_note);
    } else if (tag.cliff_note !== null && typeof tag.cliff_note === "object") {
      // If it's already an object, use it directly
      parsedNote = tag.cliff_note as CliffNote;
    }

    setCliffNote(parsedNote);
    setArticles(tag.articles || []);
    setSelectedTag(tag.tag);
    setShowCliffNote(true);
  };

  if (isLoadingTags) {
    return (
      <div className="flex flex-wrap gap-2">
        {[...Array(10)].map((_, i) => (
          <Badge key={i} variant="secondary" className="animate-pulse">
            Loading...
          </Badge>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-3 mb-4 w-full justify-center">
      <div className="flex flex-wrap gap-2 justify-center">
        {tags.length > 0 ? (
          tags.map((tag) => (
            <Badge
              key={tag.tag}
              variant="secondary"
              className={`cursor-pointer transition-all duration-200 border border-black text-sm py-2 px-4 rounded-full drop-shadow-md font-medium flex items-center
              ${
                selectedTag === tag.tag
                  ? "bg-black text-primary-foreground hover:bg-primary/90"
                  : `bg-gradient-to-br ${tagColors[tag.tag]} hover:scale-105`
              }`}
              onClick={() => handleTagClick(tag)}
            >
              <TrendingUp
                className={`w-4 h-4 mr-2 ${
                  selectedTag === tag.tag ? "text-white" : "text-black"
                }`}
              />
              <span className="font-semibold">{tag.tag}</span>{" "}
            </Badge>
          ))
        ) : (
          <div className="text-gray-500 italic">No trending tags found</div>
        )}

        {selectedTag && cliffNote && showCliffNote && (
          <div className="mt-4 w-full bg-white rounded-lg border border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
            {/* Header */}
            <div className=" p-4">
              <div className="flex justify-between items-start">
                <h3 className="text-2xl font-medium text-black tracking-tight pt-2 px-2">
                  {selectedTag}
                </h3>
                <button
                  onClick={() => setShowCliffNote(false)}
                  className="p-1 hover:bg-black/5 rounded-md transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            {/* Content */}
            <div className="px-4 pb-4">
              <div className="bg-white rounded-lg p-3">
                <div className="space-y-2">
                  <h4 className="text-md font-semibold mb-2">Big Picture</h4>
                  <p className="text-sm text-gray-800">
                    {cliffNote.main_point}
                  </p>

                  <h4 className="text-md font-semibold mt-4">Why Care</h4>
                  <p className="text-sm text-gray-800">{cliffNote.why_care}</p>
                </div>

                <div className="mt-4 space-y-4">
                  {cliffNote.key_facts && cliffNote.key_facts.length > 0 && (
                    <div className="bg-indigo-50 border border-indigo-300 rounded-md p-4">
                      <h4 className="font-bold text-sm mb-2 text-indigo-800">
                        Key Facts
                      </h4>
                      <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                        {cliffNote.key_facts.map((item, idx) => (
                          <li key={idx}>{item.fact}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {cliffNote.whats_next && cliffNote.whats_next.length > 0 && (
                    <div className="mt-4 space-y-4">
                      <h4 className="text-md font-semibold mt-4 text-gray-800">
                        What's likely next
                      </h4>
                      <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                        {cliffNote.whats_next.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <button
                  onClick={() => setShowArticles((prev) => !prev)}
                  className="text-sm font-medium text-gray-600 hover:underline transition-all cursor-pointer inline-flex items-center"
                >
                  {(() => {
                    const uniqueAuthors = [
                      ...new Set(
                        articles.map((item) => item.author).filter(Boolean)
                      ),
                    ];
                    const displayAuthors = uniqueAuthors.slice(0, 3);

                    if (uniqueAuthors.length === 0) {
                      return "Getting attention across multiple sources";
                    } else if (uniqueAuthors.length === 1) {
                      return `Getting attention on ${displayAuthors[0]}`;
                    } else if (uniqueAuthors.length <= 3) {
                      return `Getting attention on ${displayAuthors.join(
                        ", "
                      )}`;
                    } else {
                      return `Getting attention on ${displayAuthors.join(
                        ", "
                      )} and ${uniqueAuthors.length - 3} more`;
                    }
                  })()}
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>

              {articles.length > 0 && showArticles && (
                <div className="rounded-lg p-4 shadow-sm">
                  {showArticles && (
                    <div className="space-y-2 mt-3">
                      {articles.map((item, idx) => (
                        <div key={idx} className="px-3 rounded-md">
                          <a
                            href={item.link}
                            className="text-blue-600 hover:underline text-sm"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {item.title}
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* Footer */}
          </div>
        )}
      </div>
    </div>
  );
}
